import React, { useEffect, useMemo, useState } from "react";
import api from "../services/api";
import UserAvatar from "./UserAvatar";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AdminList = () => {
  const [admins, setAdmins] = useState([]);
  const [activity, setActivity] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadAdmins = async () => {
      try {
        setLoading(true);
        setError("");
        const [adminsResponse, activityResponse] = await Promise.all([
          api.get("/users/admins"),
          api.get("/users/activity-summary?days=7"),
        ]);
        setAdmins(Array.isArray(adminsResponse.data) ? adminsResponse.data : []);
        setActivity(activityResponse.data || {});
      } catch (err) {
        console.error("Erreur chargement administrateurs :", err);
        setError("Impossible de charger les administrateurs.");
      } finally {
        setLoading(false);
      }
    };

    loadAdmins();
  }, []);

  const filteredAdmins = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return admins;
    return admins.filter((admin) =>
      [admin.Surnom, admin.Email, admin.Grade?.Nom, admin.Etat?.Nom]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle))
    );
  }, [admins, search]);

  return (
    <section className="mx-auto my-8 max-w-5xl overflow-hidden rounded-2xl border border-sky-500/10 bg-white/80 shadow-xl shadow-slate-950/5 backdrop-blur dark:bg-slate-950/70">
      <div className="border-b border-sky-500/10 bg-gradient-to-r from-sky-500/15 via-blue-500/10 to-transparent px-6 py-5">
        <p className="text-sm font-bold uppercase text-sky-500 dark:text-sky-400">Administration</p>
        <h2 className="mt-1 text-2xl font-black text-slate-950 dark:text-white">Administrateurs</h2>
      </div>

      <div className="space-y-4 p-6">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Rechercher un administrateur"
          className="w-full rounded-xl border border-sky-500/20 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-sky-400 dark:bg-slate-950/70 dark:text-white sm:max-w-sm"
        />

        {error && <p className="text-sm font-semibold text-red-600 dark:text-red-300">{error}</p>}
        {loading ? (
          <p className="text-sm font-semibold text-slate-500 dark:text-slate-300">Chargement...</p>
        ) : filteredAdmins.length === 0 ? (
          <p className="text-sm font-semibold text-slate-500 dark:text-slate-300">Aucun administrateur.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {filteredAdmins.map((admin) => {
              const adminActivity = activity[String(admin.UtilisateurID)];
              return (
                <article key={admin.UtilisateurID} className="rounded-xl border border-sky-500/10 bg-white/80 p-4 shadow-sm dark:bg-slate-950/60">
                  <div className="flex items-start gap-4">
                    <UserAvatar user={admin} className="size-14 rounded-xl object-cover" />
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-lg font-black text-slate-950 dark:text-white">{admin.Surnom}</h3>
                      <p className="truncate text-sm text-slate-500 dark:text-slate-400">{admin.Email}</p>
                      <p className="mt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
                        {admin.Grade?.Nom || `Grade #${admin.GradeID}`} - {admin.Etat?.Nom || `État #${admin.EtatID}`}
                      </p>
                      <p className="mt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
                        Dernière connexion : {formatDateTime(admin.LastLogin)}
                      </p>
                      <p className="mt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
                        Logs 7j : {adminActivity?.totalLogsLastNDays || 0}
                      </p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default AdminList;
